// @ts-nocheck
import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query/react";
import { deepMerge, throttle, isObjectEmpty } from "./Utils";
import { logoutAction, refreshAction } from "./StoreActions";
import globalSlice, {
  getGlobalSliceStorageState,
  globalInitialState,
} from "./StoreSlice";
import loginSlice, {
  getLoginSliceStorageState,
  loginInitialState,
} from "../login/LoginStoreSlice";
import {
  creditDirectApi,
  nimbleX360Api,
  nimbleX360EDRApi,
  nimbleX360WrapperApi,
  nimbleX360WrapperBulkWriteOffApi,
  sequestApi,
  publicApi,
  nimbleX360MambuApi,
  fineractExternalApi,
  cldAuthenticationApi,
  cdlLeadsGenerationApi,
  cdlEDRReconciliationApi,
  nx360HookApi,
  neduWrapperX360Api,
  nipSupportApi,
  impairmentApi,
  zeusApi,
  outwardTransactionsApi,
  businessBankingApi,
  documentWrapperApi,
  paySyncApi,
} from "./StoreQuerySlice";
import { RtkqTagEnum } from "./Constants";
import CryptoJS from "crypto-js";

const ENCRYPTION_KEY = "12345";

const store = configureStore({
  reducer: {
    [globalSlice.name]: globalSlice.reducer,
    [loginSlice.name]: loginSlice.reducer,
    [nimbleX360Api.reducerPath]: nimbleX360Api.reducer,
    [nimbleX360EDRApi.reducerPath]: nimbleX360EDRApi.reducer,
    [sequestApi.reducerPath]: sequestApi.reducer,
    [publicApi.reducerPath]: publicApi.reducer,
    [creditDirectApi.reducerPath]: creditDirectApi.reducer,
    [nimbleX360WrapperApi.reducerPath]: nimbleX360WrapperApi.reducer,
    [nimbleX360WrapperBulkWriteOffApi.reducerPath]:
      nimbleX360WrapperBulkWriteOffApi.reducer,
    [nimbleX360MambuApi.reducerPath]: nimbleX360MambuApi.reducer,
    [fineractExternalApi.reducerPath]: fineractExternalApi.reducer,
    [cldAuthenticationApi.reducerPath]: cldAuthenticationApi.reducer,
    [cdlLeadsGenerationApi.reducerPath]: cdlLeadsGenerationApi.reducer,
    [cdlEDRReconciliationApi.reducerPath]: cdlEDRReconciliationApi.reducer,
    [nx360HookApi.reducerPath]: nx360HookApi.reducer,
    [neduWrapperX360Api.reducerPath]: neduWrapperX360Api.reducer,
    [nipSupportApi.reducerPath]: nipSupportApi.reducer,
    [impairmentApi.reducerPath]: impairmentApi.reducer,
    [zeusApi.reducerPath]: zeusApi.reducer,
    [outwardTransactionsApi.reducerPath]: outwardTransactionsApi.reducer,
    [businessBankingApi.reducerPath]: businessBankingApi.reducer,
    [paySyncApi.reducerPath]: paySyncApi.reducer,
    [documentWrapperApi.reducerPath]: documentWrapperApi.reducer,
  },
  preloadedState: loadState({
    [globalSlice.name]: globalInitialState,
    [loginSlice.name]: loginInitialState,
  }),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      nimbleX360Api.middleware,
      nimbleX360EDRApi.middleware,
      sequestApi.middleware,
      publicApi.middleware,
      creditDirectApi.middleware,
      nimbleX360MambuApi.middleware,
      nimbleX360WrapperApi.middleware,
      nimbleX360WrapperBulkWriteOffApi.middleware,
      fineractExternalApi.middleware,
      cldAuthenticationApi.middleware,
      cdlLeadsGenerationApi.middleware,
      cdlEDRReconciliationApi.middleware,
      nx360HookApi.middleware,
      neduWrapperX360Api.middleware,
      nipSupportApi.middleware,
      impairmentApi.middleware,
      businessBankingApi.middleware,
      paySyncApi.middleware,
      zeusApi.middleware,
      outwardTransactionsApi.middleware,
      documentWrapperApi.middleware,
      rtkqOnResetMiddleware(
        nimbleX360Api,
        nimbleX360EDRApi,
        sequestApi,
        publicApi,
        creditDirectApi,
        nimbleX360MambuApi,
        nimbleX360WrapperApi,
        nimbleX360WrapperBulkWriteOffApi,
        fineractExternalApi,
        cldAuthenticationApi,
        cdlLeadsGenerationApi,
        cdlEDRReconciliationApi,
        nx360HookApi,
        neduWrapperX360Api,
        nipSupportApi,
        impairmentApi,
        zeusApi,
        outwardTransactionsApi,
        businessBankingApi,
        paySyncApi,
        documentWrapperApi
      )
    ),
});

setupListeners(store.dispatch);

store.subscribe(
  throttle(() => {
    const state = store.getState();
    saveState({
      [globalSlice.name]: getGlobalSliceStorageState(state[globalSlice.name]),
      [loginSlice.name]: getLoginSliceStorageState(state[loginSlice.name]),
    });
  }, 1000)
);

export default store;

function saveState(state) {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem(
      "@state",
      CryptoJS.AES.encrypt(serializedState, ENCRYPTION_KEY)
    );
  } catch (error) {}
}

function loadState(initialState = {}) {
  try {
    const newState = Object.assign({}, initialState);
    const storageState = getLocalStorageState();
    if (storageState && !isObjectEmpty(storageState)) {
      Object.assign(newState, deepMerge(newState, storageState));
    }
    return newState;
  } catch (error) {}
  return undefined;
}

function getLocalStorageState() {
  const serializedState = localStorage.getItem("@state");
  if (serializedState) {
    return JSON.parse(
      CryptoJS.AES.decrypt(serializedState, ENCRYPTION_KEY).toString(
        CryptoJS.enc.Utf8
      )
    );
  }
  return null;
}

export function rtkqOnResetMiddleware(...apis) {
  return (store) => (next) => (action) => {
    const result = next(action);
    if (logoutAction.match(action)) {
      for (const api of apis) {
        store.dispatch(api.util.resetApiState());
      }
      localStorage.clear();
      window?.postMessage?.({ type: "LOGOUT" }, window.location.origin);
    }
    if (refreshAction.match(action)) {
      for (const api of apis) {
        store.dispatch(api.util.invalidateTags(Object.values(RtkqTagEnum)));
      }
    }

    return result;
  };
}
